<template>
  <div class="tw-flex-wrap tw-mb-1">
    <WdsLabel v-for="item in modelValue" :key="item.label" sm class="tw-mr-1">
      <div class="tw-flex tw-items-center tw-gap-xs">
        {{ item.label }}
        <button class="label-button" @click="() => handleRemove(item)">
          <WdsIcon name="close" :size="12" />
        </button>
      </div>
    </WdsLabel>
  </div>
  <WdsSelect
    v-model:search="searchInput"
    searchable
    :list="list"
    :error="suggestionError"
    :name="name"
    data-test="job-industries-dropdown"
    :can-deselect="canDeselect"
    @update:model-value="handleUpdate"
  >
    <template #prefix>
      <WdsSpinner v-if="loading" indeterminate :size="16" />
      <WdsIcon v-else name="search" class="inline" :size="16" />
    </template>
    <template #header>
      <div class="tw-text-gray-600">{{ placeholder }}</div>
    </template>
  </WdsSelect>
</template>

<script setup lang="ts">
import { WdsSelect, WdsLabel, WdsIcon, WdsSpinner } from '@worksome/wds-components'
import { ref, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { IndustriesQuery } from '@/components/TypeSuggestion/graphql/queries'
import { $t } from '@/composables/translation'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const emit = defineEmits(['update:model-value'])

const searchInput = ref('')

type Option = {
  label: string
  value?: string
}

const props = withDefaults(
  defineProps<{
    modelValue: Option[]
    error?: string | boolean
    placeholder?: string
    name?: string
    canDeselect?: boolean
  }>(),
  {
    error: undefined,
    placeholder: $t('Add an industry'),
    name: undefined,
    canDeselect: false,
  },
)

const {
  result,
  loading,
  error: queryError,
} = useQuery(IndustriesQuery, { first: PER_PAGE_MAXIMUM }, { clientId: 'publicApi' })

const list = computed(
  () =>
    result?.value?.industries.data
      .map(({ name, id }) => ({ label: name, value: id }))
      .filter(({ value }) => !props.modelValue.find((item) => item.value === value)) || [],
)

const handleUpdate = (option: Option) => {
  emit('update:model-value', [...props.modelValue, option])
}

const handleRemove = (option: Option) => {
  emit(
    'update:model-value',
    props.modelValue.filter(({ value }) => value !== option.value),
  )
}

const suggestionError = computed(() => props.error || queryError.value?.message)
</script>

<style scoped lang="scss">
.label-button {
  @apply tw-bg-transparent;
  @apply tw-border-none;
  @apply tw-p-0;
  @apply tw-cursor-pointer;
}

:deep(.wds-select-wrapper .wds-select-button-display) {
  @apply tw-pl-2;
}

:deep(.wds-select-wrapper button.icon-down) {
  @apply tw-hidden;
}
</style>
